import { Button, Cell, Layout, Notification, Text } from '@wix/design-system'
import {
  ChoiceQuestionContentComponent,
  GroupContentComponent,
  useContent,
  useNext,
  usePageLoading,
} from '@wix/funnel-builder-lizard'
import cs from 'classnames'
import React, { FC, useEffect, useState } from 'react'

import { usePageLogger } from '../../../bi/mfi'
import { DesignSystemTokensLoader } from '../../reusable/DesignSystemTokensLoader'
import { RichText } from '../../reusable/RichText'
import { WixMobileCheckToggle } from '../../reusable/WixMobileCheckToggle/WixMobileCheckToggle'
import { WixMobileContentWrapper } from '../../reusable/WixMobileContentWrapper/WixMobileContentWrapper'
import { WixMobileDescription } from '../../reusable/WixMobileDescription/WixMobileDescription'
import { WixMobileFooter } from '../../reusable/WixMobileFooter/WixMobileFooter'
import { WixMobileHeader } from '../../reusable/WixMobileHeader/WixMobileHeader'
import { WixMobileHeading } from '../../reusable/WixMobileHeading/WixMobileHeading'
import { WixMobilePage } from '../../reusable/WixMobilePage/WixMobilePage'
import { WixMobileSkipButton } from '../../reusable/WixMobileSkipButton/WixMobileSkipButton'
import { WixMobileStepProgressBar } from '../../reusable/WixMobileStepProgressBar/WixMobileStepProgressBar'
import { wixMobileWelcomeDataHooks as dataHooks } from './WixMobileWelcome.dataHooks'
import s from './WixMobileWelcome.scss'

export type Content = {
  skip: string
  hasStepProgressBar: boolean
  stepProgressBar: GroupContentComponent<{ total: number; current: number }>
  question: ChoiceQuestionContentComponent<
    { title: string; subtitle: string },
    {
      label: string
    }
  >
  next: string
  disclaimer: string
  successMessage: string
  skin?: 'harmony' | 'harmony-v3'
}

type Option = {
  optionKey: string
  label: string
}

export const WixMobileWelcome: FC = () => {
  const { content } = useContent<Content>()
  const pageLogger = usePageLogger()
  const [isPageLoading] = usePageLoading()
  const next = useNext()
  const [selectedOption, setSelectedOption] = useState<Option>()

  const handleSkip = () => {
    if (isPageLoading) {
      return
    }
    void pageLogger.skip()
    next({ [content.question.questionKey]: { skipped: true } })
  }

  const handleNext = async () => {
    if (isPageLoading) {
      return
    }
    await pageLogger.answer({ answer: selectedOption!.optionKey })
    next({ [content.question.questionKey]: { values: [selectedOption!.optionKey] } })
  }

  const isNextDisabled = !selectedOption?.optionKey

  useEffect(() => {
    void pageLogger.screenView()
  }, [pageLogger])

  const body = (
    <>
      <WixMobileHeader
        skin="harmony"
        rightContent={
          (content.skip && (
            <WixMobileSkipButton onSkip={handleSkip} label={content.skip} dataHook={dataHooks.skip()} />
          )) ||
          undefined
        }
      />
      {content.successMessage && (
        <Notification theme="success" show dataHook={dataHooks.successMessage()}>
          <Notification.TextLabel>{content.successMessage}</Notification.TextLabel>
        </Notification>
      )}
      <WixMobileContentWrapper>
        {content.hasStepProgressBar && (
          <WixMobileStepProgressBar
            current={content.stepProgressBar.content.current}
            total={content.stepProgressBar.content.total}
            dataHook={dataHooks.stepProgressBar()}
          />
        )}
        <div role="radiogroup" aria-labelledby="description">
          <WixMobileHeading dataHook={dataHooks.title()} skin={content.skin ?? 'harmony'}>
            {content.question.content.title}
          </WixMobileHeading>
          <WixMobileDescription dataHook={dataHooks.description()} skin="harmony">
            <span id="description">{content.question.content.subtitle}</span>
          </WixMobileDescription>
          <Layout cols={1} gap="12px">
            {content.question.options.map(option => {
              const isChecked = selectedOption?.optionKey === option.optionKey
              const labelId = `label-${option.optionKey}`

              return (
                <Cell key={option.optionKey}>
                  <div
                    className={cs(s.radioButton, { [s.checked]: isChecked })}
                    onClick={() => setSelectedOption(option)}
                    data-hook={dataHooks.option({ key: option.optionKey, isChecked })}
                    role="radio"
                    aria-checked={isChecked}
                    aria-labelledby={labelId}
                    tabIndex={0}
                  >
                    <WixMobileCheckToggle isChecked={isChecked} skin="harmony" />
                    <Text
                      id={labelId}
                      dataHook={dataHooks.optionFormField({ key: option.optionKey })}
                      className={s.optionLabel}
                    >
                      {option.label}
                    </Text>
                  </div>
                </Cell>
              )
            })}
          </Layout>
        </div>
      </WixMobileContentWrapper>
      {(content.next || content.disclaimer) && (
        <WixMobileFooter>
          {content.next && (
            <Button
              size="large"
              fullWidth
              disabled={isNextDisabled}
              onClick={handleNext}
              dataHook={dataHooks.next({ isDisabled: isNextDisabled })}
              className={s.nextButton}
            >
              {content.next}
            </Button>
          )}
          {content.disclaimer && (
            <RichText light secondary size="tiny" className={s.disclaimer} dataHook={dataHooks.disclaimer()}>
              {content.disclaimer}
            </RichText>
          )}
        </WixMobileFooter>
      )}
    </>
  )

  return (
    <DesignSystemTokensLoader type="odeditor">
      <WixMobilePage>
        <div className={s.root}>{body}</div>
      </WixMobilePage>
    </DesignSystemTokensLoader>
  )
}
